import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  AppBar,
  Toolbar,
  Avatar,
  Menu,
  MenuItem,
  Chip,
  IconButton,
  Tabs,
  Tab,
  Badge
} from '@mui/material';
import {
  NotificationsOutlined,
  Assignment,
  CheckCircle,
  Schedule
} from '@mui/icons-material';
import { Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/authService';
import { apiService } from '../services/api';
import logo from '../assets/logo.png';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const StaffDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [assignedProjects, setAssignedProjects] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const userInfo = localStorage.getItem('userInfo');
    
    if (!token) {
      navigate('/login');
      return;
    }
    
    if (userInfo) {
      const userData = JSON.parse(userInfo);
      if (!userData.is_staff) {
        navigate('/dashboard');
        return;
      }
      setUser(userData);
      loadAssignedProjects(userData.username);
    }

    // Load unread message count
    loadUnreadCount();

    // Poll for new messages every 30 seconds
    const interval = setInterval(loadUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [navigate]);

  const loadUnreadCount = async () => {
    try {
      const response = await apiService.getUnreadMessageCount();
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Error loading unread count:', error);
    }
  };

  const loadAssignedProjects = async (staffName) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await axios.get(
        `${API_URL}/projects/admin/all/?consultant=${encodeURIComponent(staffName)}`,
        { headers: { Authorization: `Token ${token}` } }
      );
      
      const projects = response.data.results || response.data || [];
      console.log('Loaded assigned projects:', projects);
      setAssignedProjects(projects);
    } catch (error) {
      console.error('Error loading assigned projects:', error);
      setAssignedProjects([]);
    }
  };

  const handleLogout = async () => {
    try {
      await authService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      navigate('/login');
    }
  };

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (newValue === 1) navigate('/staff/projects');
    if (newValue === 2) navigate('/staff/messages');
  };

  return (
    <Box sx={{ flexGrow: 1, bgcolor: '#f8f9fa', minHeight: '100vh' }}>
      <AppBar position="static" sx={{ bgcolor: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        <Toolbar sx={{ px: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 4 }}>
            <img src={logo} alt="TICSPL" style={{ height: '32px', marginRight: '12px' }} />
            <Typography variant="h6" sx={{ color: '#1976d2', fontWeight: 600 }}>
              TICSPL Staff Portal
            </Typography>
          </Box>
          
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            sx={{ 
              flexGrow: 1,
              '& .MuiTab-root': {
                color: '#666',
                fontWeight: 500,
                textTransform: 'none',
                fontSize: '16px'
              },
              '& .Mui-selected': {
                color: '#1976d2 !important'
              }
            }}
          >
            <Tab label="Dashboard" />
            <Tab label="Projects" />
            <Tab label="Messages" />
          </Tabs>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton sx={{ color: '#666' }} onClick={() => navigate('/staff/messages')}>
              <Badge badgeContent={unreadCount} color="error">
                <NotificationsOutlined />
              </Badge>
            </IconButton>
            <Button
              color="inherit"
              onClick={handleMenuOpen}
              sx={{ 
                color: '#333',
                textTransform: 'none',
                fontWeight: 500
              }}
              endIcon={<Avatar sx={{ width: 32, height: 32, bgcolor: '#1976d2' }}>{user?.username?.charAt(0).toUpperCase()}</Avatar>}
            >
              {user?.username}
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              <MenuItem onClick={handleMenuClose}>Profile</MenuItem>
              <MenuItem onClick={handleLogout}>Logout</MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 600, color: '#333', mb: 1 }}>
            Welcome, {user?.username} 👋
          </Typography>
          <Typography variant="h6" sx={{ color: '#666', fontWeight: 400 }}>
            Specialization: {user?.specialization || user?.profile?.specialization || 'Not specified'}
          </Typography>
        </Box>

        <Grid container spacing={4}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Assignment sx={{ fontSize: 40, color: '#1976d2', mb: 2 }} />
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                  {user?.assignedProjects?.length || assignedProjects.length}
                </Typography>
                <Typography color="text.secondary">Assigned Projects</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <CheckCircle sx={{ fontSize: 40, color: '#4caf50', mb: 2 }} />
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                  {assignedProjects.filter(p => p.status === 'Approved').length}
                </Typography>
                <Typography color="text.secondary">Completed</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Schedule sx={{ fontSize: 40, color: '#ff9800', mb: 2 }} />
                <Typography variant="h4" sx={{ fontWeight: 600 }}>
                  {assignedProjects.filter(p => p.status === 'Under Review').length}
                </Typography>
                <Typography color="text.secondary">In Progress</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Box sx={{ mt: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
            My Assigned Projects
          </Typography>
          
          {assignedProjects.length === 0 ? (
            <Card sx={{ textAlign: 'center', py: 6 }}>
              <CardContent>
                <Assignment sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  No projects assigned yet
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Contact your administrator for project assignments
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <Grid container spacing={3}>
              {assignedProjects.map((project) => (
                <Grid item xs={12} md={6} key={project.id}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {project.name}
                        </Typography>
                        <Chip 
                          label={project.status} 
                          color={project.status === 'Approved' ? 'success' : project.status === 'Rejected' ? 'error' : 'warning'}
                        />
                      </Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Type: {project.type}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Investment:</strong> {project.investment}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        <strong>Location:</strong> {project.location}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Submitted:</strong> {new Date(project.submittedAt).toLocaleDateString()}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default StaffDashboard;